// Ramp placement helper (elevation feature). A ramp bridges ONE cliff edge, so
// rather than blind rotation the client auto-orients it toward an adjacent cliff
// and lets R cycle through the valid edges — mirroring the server's canPlace rule.
import type { GameState } from './state'
import { DIRS } from '../shared/hex'
import { Terrain, isPassable } from '../shared/terrain'
import { tooSteep } from '../shared/elevation'
import { terrainAt, elevAt } from './render/terrain'

/** DIRS indices from (q,r) that a ramp could bridge: a Δ>1 tier step onto
 *  passable land. Empty on flat worlds (no elevation) or away from any cliff. */
export function rampCliffDirs(st: GameState, q: number, r: number): number[] {
  const t0 = elevAt(st, q, r)
  if (t0 === undefined) return []
  const out: number[] = []
  for (let d = 0; d < 6; d++) {
    const nq = q + DIRS[d].q
    const nr = r + DIRS[d].r
    const tn = elevAt(st, nq, nr)
    const nt = terrainAt(st, nq, nr)
    if (tn === undefined || nt === undefined || !isPassable(nt)) continue
    // The high country is already climbable (no ramp needed / server refuses it).
    if (nt === Terrain.Hills || nt === Terrain.Mountain) continue
    const t = terrainAt(st, q, r)
    if (t === Terrain.Hills || t === Terrain.Mountain) continue
    if (tooSteep(t0, tn)) out.push(d)
  }
  return out
}

/** The ramp's chosen facing at (q,r), cycled through the valid cliff edges by the
 *  R key (or the ⟳ chip). null when there's no cliff here to bridge. */
export function rampDir(st: GameState, q: number, r: number, rotate: number): number | null {
  const dirs = rampCliffDirs(st, q, r)
  if (!dirs.length) return null
  return dirs[((rotate % dirs.length) + dirs.length) % dirs.length]
}
